import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  ShoppingCart,
  ClipboardList,
  Upload,
  Database,
  FileText,
  Star,
  QrCode,
  Settings,
  Menu,
  X,
  Home,
  LogOut,
  Bell,
  User,
  Users,
  ExternalLink,
  ChevronUp,
  Clock,
  Trash2,
  Loader,
  Code,
  Plane,
  ListChecks,
  Tags,
  ArrowLeftRight,
  Mail,
  Torus
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import { CART_UPDATED_EVENT } from '../../utils/cartEvents';
import useHiverChat from '../../hooks/useHiverChat';
import NotificationTimeline from '../NotificationTimeline';
import AutocompleteInput from '../AutocompleteInput';
import ChangelogWidget from '../shared/ChangelogWidget';
import ChangelogModal from '../shared/ChangelogModal';
import OrderingDisabledBanner from '../shared/OrderingDisabledBanner';
import { VERSION_LABEL, BUILD_HASH, BUILD_TIME } from '../../version';

// Every page renders its own UserLayout, so it remounts on each navigation. Caching the account at
// module level keeps role-gated menu items (Seal Lookup) and the profile footer from vanishing while
// it re-fetches. Same pattern as ConsoleLayout; cleared on sign-out or a different user signing in.
let cachedAccount = null;
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || (cachedAccount && session?.user?.id !== cachedAccount.id)) {
    cachedAccount = null;
  }
});

// Self-contained airbag cutoff countdown. Kept as its own component so its
// per-second timer only re-renders this small badge, not the whole layout.
const AirbagCountdown = () => {
  const calculate = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const hours = now.getHours();

    let targetDate = new Date(now);

    // If it's weekend, set target to next Monday
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      targetDate.setDate(targetDate.getDate() + (8 - dayOfWeek) % 7);
      targetDate.setHours(11, 0, 0, 0);
    }
    // If it's after 11 AM on a weekday, set target to the next business day
    // (Friday afternoon rolls over to Monday, not Saturday)
    else if (hours >= 11) {
      targetDate.setDate(targetDate.getDate() + (dayOfWeek === 5 ? 3 : 1));
      targetDate.setHours(11, 0, 0, 0);
    }
    // If it's before 11 AM on a weekday, set target to today at 11 AM
    else {
      targetDate.setHours(11, 0, 0, 0);
    }

    const timeDiff = targetDate - now;
    const hoursRemaining = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    let color = 'green';
    if (hoursRemaining < 1) {
      color = 'red';
    } else if (hoursRemaining < 4) {
      color = 'yellow';
    }

    return { timeRemaining: `${hoursRemaining}h ${minutesRemaining}m`, color };
  };

  const [countdown, setCountdown] = useState(calculate);

  useEffect(() => {
    const timer = setInterval(() => setCountdown(calculate()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className={`flex items-center px-3 py-1.5 rounded-lg border ${
        countdown.color === 'red' ? 'bg-red-50 border-red-200' :
        countdown.color === 'yellow' ? 'bg-yellow-50 border-yellow-200' :
        'bg-green-50 border-green-200'
      }`}
      title="Time left to order for today's air freight (cutoff 11:00 AM, business days)"
    >
      <Plane className={`h-4 w-4 mr-2 ${
        countdown.color === 'red' ? 'text-red-600' :
        countdown.color === 'yellow' ? 'text-yellow-600' :
        'text-green-600'
      }`} aria-hidden="true" />
      <span className={`text-xs font-medium whitespace-nowrap ${
        countdown.color === 'red' ? 'text-red-700' :
        countdown.color === 'yellow' ? 'text-yellow-700' :
        'text-green-700'
      }`}>
        <span className="hidden lg:inline">Air freight cutoff </span>
        <span className="tabular-nums">{countdown.timeRemaining}</span>
      </span>
    </div>
  );
};

// Header icon badge. Cart uses the brand colour (it's not an alert);
// notifications use red. Counts are capped so the badge never overflows.
const CountBadge = ({ count, tone = 'alert' }) => {
  if (!count) return null;
  const toneClass = tone === 'brand' ? 'bg-brand text-brand-dark' : 'bg-red-600 text-white';
  return (
    <span
      className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold leading-none flex items-center justify-center ring-2 ring-white tabular-nums ${toneClass}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

const UserLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentUser, setCurrentUser] = useState(cachedAccount);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [showChangelog, setShowChangelog] = useState(false);
  
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountMenuRef = useRef(null);
  const searchHistoryRef = useRef(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [userId, setUserId] = useState(null);
  const [activeHistoryTab, setActiveHistoryTab] = useState('recent');
  const [isSearching, setIsSearching] = useState(false);

  const history = useHistory();
  const location = useLocation();

  useHiverChat(currentUser);

  useEffect(() => {
    fetchCurrentUser();
    fetchUnreadNotificationsCount();
    loadCartItems();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Set userId immediately when we get the user
        setUserId(user.id);
        
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        cachedAccount = data;
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      showError(error, 'Failed to fetch user data');
    }
  };

  const fetchUnreadNotificationsCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { count, error } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('read', false);

        if (error) throw error;
        setUnreadNotificationsCount(count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread notifications count:', error);
    }
  };

  const loadCartItems = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItemCount(cart.length);
  };

  // Listen for storage changes to update cart count
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'cart') {
        loadCartItems();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for focus events to refresh cart when returning to tab
    const handleFocus = () => {
      loadCartItems();
    };

    window.addEventListener('focus', handleFocus);
    // `storage` only fires in other tabs; this covers changes made in this one.
    window.addEventListener(CART_UPDATED_EVENT, loadCartItems);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(CART_UPDATED_EVENT, loadCartItems);
    };
  }, []);

  // Prevent the page behind the mobile sidebar / search from scrolling while
  // either is open. Without this the underlying page scrolls behind the drawer.
  useEffect(() => {
    const lockScroll = isMobile && (sidebarOpen || showMobileSearch);
    const previousOverflow = document.body.style.overflow;
    if (lockScroll) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = previousOverflow || '';
    };
  }, [isMobile, sidebarOpen, showMobileSearch]);

  // Close the mobile drawer and account menu automatically when navigating between pages.
  useEffect(() => {
    setSidebarOpen(false);
    setShowAccountMenu(false);
  }, [location.pathname]);

  // Close the account menu on outside click or Escape.
  useEffect(() => {
    if (!showAccountMenu) return undefined;
    const onPointerDown = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setShowAccountMenu(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowAccountMenu(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showAccountMenu]);

  // Close the search history dropdown on outside click or Escape.
  useEffect(() => {
    if (!showSearchHistory) return undefined;
    const onPointerDown = (e) => {
      if (searchHistoryRef.current && !searchHistoryRef.current.contains(e.target)) {
        setShowSearchHistory(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowSearchHistory(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showSearchHistory]);

  // Escape closes whichever overlay is open (drawer, mobile search, modals).
  useEffect(() => {
    if (!(sidebarOpen || showMobileSearch || showNotifications)) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      setSidebarOpen(false);
      setShowMobileSearch(false);
      setShowNotifications(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [sidebarOpen, showMobileSearch, showNotifications]);

  // "/" focuses the header part search from anywhere (unless already typing).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement;
      const typing = el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
      if (typing) return;
      const input = document.querySelector('[data-global-search] input');
      if (input) {
        e.preventDefault();
        input.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      cachedAccount = null;
      showNotification('Successfully signed out!', 'success');
      history.push('/login');
    } catch (error) {
      showError(error, 'Failed to sign out');
    }
  };

  // Search functionality
  const fetchSearchHistory = useCallback(async () => {
    if (!userId) {
      console.log('No userId available for search history fetch');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('search_history')
        .select('part_number, search_type')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setSearchHistory(data || []);
      console.log('Search history fetched:', data?.length || 0, 'items');
    } catch (error) {
      console.error('Error fetching search history:', error);
      showError(error, 'Failed to load search history');
    }
  }, [userId]);

  const fetchFavorites = useCallback(async () => {
    if (!userId) {
      console.log('No userId available for favorites fetch');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('part_number')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFavorites(data ? data.map(item => item.part_number) : []);
      console.log('Favorites fetched:', data?.length || 0, 'items');
    } catch (error) {
      console.error('Error fetching favorites:', error);
      showError(error, 'Failed to load favourites');
    }
  }, [userId]);

  // One search behaviour everywhere: Part Enquiry tries an exact part-number
  // match first and falls back to smart search when there is none.
  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;

    setIsSearching(true);

    // Add to search history
    if (userId) {
      try {
        const { error } = await supabase
          .from('search_history')
          .insert({ user_id: userId, part_number: term, search_type: 'exact' });

        if (error) {
          console.error('Error adding search to history:', error);
        } else {
          // Refresh search history to show the new search
          fetchSearchHistory();
        }
      } catch (error) {
        console.error('Error adding to search history:', error);
      }
    }

    // Store loading context for UserPartEnquiry to pick up
    sessionStorage.setItem('searchLoading', JSON.stringify({
      isLoading: true,
      searchTerm: term,
      searchType: 'exact',
      timestamp: Date.now()
    }));

    history.push(`/user/part-enquiry?partNumber=${encodeURIComponent(term)}`);

    // Close mobile search modal and search history
    setShowMobileSearch(false);
    setShowSearchHistory(false);
  }, [searchTerm, history, userId, fetchSearchHistory]);

  const handleSearchSuggestionSelect = async (suggestion) => {
    setSearchTerm(suggestion.part_number);
    setIsSearching(true);
    
    // Add to search history
    if (userId) {
      try {
        const { error } = await supabase
          .from('search_history')
          .insert({ 
            user_id: userId, 
            part_number: suggestion.part_number, 
            search_type: 'exact' 
          });

        if (error) {
          console.error('Error adding search to history:', error);
        } else {
          // Refresh search history to show the new search
          fetchSearchHistory();
        }
      } catch (error) {
        console.error('Error adding to search history:', error);
      }
    }
    
    // Store loading context for UserPartEnquiry to pick up
    sessionStorage.setItem('searchLoading', JSON.stringify({
      isLoading: true,
      searchTerm: suggestion.part_number,
      searchType: 'exact',
      timestamp: Date.now()
    }));
    
    history.push(`/user/part-enquiry?partNumber=${encodeURIComponent(suggestion.part_number)}`);
    setShowMobileSearch(false);
    setShowSearchHistory(false);
  };

  const handleHistoryItemClick = (item) => {
    setSearchTerm(item.part_number);
    setIsSearching(true);
    
    // Store loading context for UserPartEnquiry to pick up
    sessionStorage.setItem('searchLoading', JSON.stringify({
      isLoading: true,
      searchTerm: item.part_number,
      searchType: item.search_type,
      timestamp: Date.now()
    }));
    
    if (item.search_type === 'exact') {
      history.push(`/user/part-enquiry?partNumber=${encodeURIComponent(item.part_number)}`);
    } else {
      history.push(`/user/part-enquiry?search=${encodeURIComponent(item.part_number)}&type=${item.search_type}`);
    }
    setShowSearchHistory(false);
    setShowMobileSearch(false);
  };

  const handleRemoveFromHistory = async (partNumber) => {
    if (userId) {
      try {
        const { error } = await supabase
          .from('search_history')
          .delete()
          .eq('user_id', userId)
          .eq('part_number', partNumber);

        if (error) throw error;
        setSearchHistory(prev => prev.filter(item => item.part_number !== partNumber));
        showNotification('Removed from search history', 'success');
      } catch (error) {
        console.error('Error removing from history:', error);
        showError(error, 'Failed to remove from history');
      }
    }
  };

  const handleRemoveFromFavorites = async (partNumber) => {
    if (userId) {
      try {
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', userId)
          .eq('part_number', partNumber);

        if (error) throw error;
        setFavorites(prev => prev.filter(fav => fav !== partNumber));
        showNotification('Removed from favourites', 'success');
      } catch (error) {
        console.error('Error removing from favorites:', error);
        showError(error, 'Failed to remove from favourites');
      }
    }
  };

  // Load search data when user is available
  useEffect(() => {
    if (userId) {
      fetchSearchHistory();
      fetchFavorites();
    }
  }, [userId, fetchSearchHistory, fetchFavorites]);

  // Reset search loading state when location changes
  useEffect(() => {
    setIsSearching(false);
  }, [location]);

  // Memoize menu items to prevent unnecessary re-renders
  const menuItems = React.useMemo(() => [
    {
      section: null,
      items: [
        { path: '/user/dashboard', icon: Home, label: 'Dashboard' },
      ]
    },
    {
      section: 'Search & Orders',
      items: [
        { path: '/user/part-enquiry', icon: Search, label: 'Part Enquiry' },
        // shortcut: staff/admin only while Seal Lookup runs on demo data; show to everyone once it reads Pimcore.
        ...(currentUser?.is_staff || currentUser?.is_admin ? [{ path: '/user/seal-lookup', icon: Torus, label: 'Seal Lookup' }] : []),
        { path: '/user/recent-orders', icon: ClipboardList, label: 'Recent Orders' },
      ]
    },
    {
      section: 'Tools',
      items: [
        { path: '/user/bulk-upload', icon: Upload, label: 'Bulk Order Upload' },
        { path: '/user/bulk-query', icon: Database, label: 'Bulk Query' },
        { path: '/user/part-number-management', icon: Tags, label: 'Part Number Mgmt' },
        { path: '/user/inquiry-lists', icon: ListChecks, label: 'Inquiry Lists (RFQ)' },
        ...(isMobile ? [{ path: '/user/scanner', icon: QrCode, label: 'Scanner' }] : [])
      ]
    },
    { 
      section: 'Resources',
      items: [
        { path: '/user/product-documents', icon: FileText, label: 'Product Documents' },
        { path: '/user/slsp-promo', icon: Star, label: 'SLSP Promo' },
        { path: '/user/contact', icon: Mail, label: 'Contact Us' },
      ]
    }
  ], [isMobile, currentUser]);

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const SidebarContent = () => {
    return (
      <div className="flex flex-col h-full">
        {/* Header - Clickable */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button
            onClick={() => history.push('/user/dashboard')}
            className="flex items-center hover:bg-gray-100 rounded-lg p-2 transition-colors duration-200 cursor-pointer"
          >
            <img src="/Logo2.png" alt="MHS Logo" className="h-8 w-auto mr-3" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Partner Portal</h2>
          </div>
        </button>
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2.5 -mr-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Close sidebar"
          >
            <X className="w-6 h-6" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Navigation - scrolls when sections are expanded past the viewport */}
      <nav className="flex-1 px-4 py-4 space-y-5 overflow-y-auto" aria-label="Main navigation">
        {menuItems.map((section) => (
          <div key={section.section || 'home'}>
            {section.section && (
              <h3 className="px-3 mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {section.section}
              </h3>
            )}
            {(
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActiveRoute(item.path);
                  
                  return item.external ? (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      onClick={() => {
                        if (isMobile) setSidebarOpen(false);
                      }}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.label}
                    </a>
                  ) : (
                    <button
                      key={item.path}
                      onClick={() => {
                        history.push(item.path);
                        if (isMobile) setSidebarOpen(false);
                      }}
                      aria-current={active ? 'page' : undefined}
                      className={`relative w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors duration-200 ${
                        active
                          ? 'bg-gray-100 text-gray-900 font-semibold'
                          : 'text-gray-700 font-medium hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      {/* Active = grey fill + brand bar; solid yellow is kept for primary actions */}
                      {active && (
                        <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-brand" aria-hidden="true" />
                      )}
                      <Icon className="w-5 h-5 mr-3" aria-hidden="true" />
                      {item.label}
                      {item.badge && (
                        <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] h-5 flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-200 space-y-2">
        {/* Role switches: neutral outline so they don't read as warnings */}
        {currentUser?.is_admin && (
          <button
            onClick={() => history.push('/admin')}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            <ArrowLeftRight className="w-4 h-4 mr-3 text-gray-500" aria-hidden="true" />
            Switch to Admin Panel
          </button>
        )}
        {currentUser?.is_company_admin && !currentUser?.is_admin && (
          <button
            onClick={() => history.push('/admin/users')}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            <Users className="w-4 h-4 mr-3 text-gray-500" aria-hidden="true" />
            Manage Company Users
          </button>
        )}
        {currentUser?.is_staff && !currentUser?.is_admin && (
          <button
            onClick={() => history.push('/staff-area')}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            <ArrowLeftRight className="w-4 h-4 mr-3 text-gray-500" aria-hidden="true" />
            Switch to Staff Area
          </button>
        )}

        {/* Account menu: profile card opens Account / Settings / Sign out */}
        <div className="relative" ref={accountMenuRef}>
          {showAccountMenu && (
            <div
              role="menu"
              className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50"
            >
              <button
                role="menuitem"
                onClick={() => history.push('/account')}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <User className="w-4 h-4 mr-3 text-gray-500" aria-hidden="true" />
                Account Overview
              </button>
              <button
                role="menuitem"
                onClick={() => history.push('/user/settings')}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Settings className="w-4 h-4 mr-3 text-gray-500" aria-hidden="true" />
                User Settings
              </button>
              <button
                role="menuitem"
                onClick={() => history.push('/user/developer')}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Code className="w-4 h-4 mr-3 text-gray-500" aria-hidden="true" />
                Developer Portal (API)
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button
                role="menuitem"
                onClick={handleSignOut}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700"
              >
                <LogOut className="w-4 h-4 mr-3" aria-hidden="true" />
                Sign Out
              </button>
            </div>
          )}
          <button
            onClick={() => setShowAccountMenu((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={showAccountMenu}
            className="w-full flex items-center px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            {currentUser?.avatar_url ? (
              <img
                src={currentUser.avatar_url}
                alt=""
                className="w-9 h-9 rounded-full object-cover border border-gray-200"
              />
            ) : (
              <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center border border-gray-200">
                <span className="text-sm text-gray-800 font-semibold">
                  {currentUser?.full_name?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                </span>
              </div>
            )}
            <div className="ml-3 flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-gray-900 truncate">
                {currentUser?.full_name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {currentUser?.company_name || currentUser?.email}
              </p>
            </div>
            <ChevronUp
              className={`w-4 h-4 text-gray-400 transition-transform ${showAccountMenu ? '' : 'rotate-180'}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
    );
  };

  const UserFooter = () => {
    return (
      <footer className="bg-white border-t border-gray-200 mt-auto flex-shrink-0">
        <div className="px-4 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
            {/* Left: Company Info */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <img src="/Logo2.png" alt="MHS Logo" className="h-6 w-auto mr-2" />
                <span className="font-semibold text-gray-900">MHS</span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-gray-300"></div>
              <a
                href="https://partner-portal-guide.readthedocs.io/en/latest/getting-started/login.html#accessing-the-portal"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-xs text-gray-600 hover:text-brand transition-colors duration-200 group"
                title="Open User Guide"
              >
                <FileText className="w-3.5 h-3.5 mr-1" />
                User Guide
                <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </a>
              <div className="hidden sm:block h-4 w-px bg-gray-300"></div>
              <p className="text-xs text-gray-600">
                Business Hours: Mon-Fri 7:30 AM - 5:00 PM AWST
              </p>
            </div>

            {/* Right: STAUFF Logo and Version */}
            <div className="flex items-center space-x-4">
              <img
                src="/logo-stauff.png"
                alt="STAUFF"
                className="h-6 w-auto opacity-75"
              />
              <div className="hidden sm:block h-4 w-px bg-gray-300"></div>
              <p className="text-xs text-gray-500" title={BUILD_TIME ? `Build ${BUILD_HASH} (${BUILD_TIME})` : BUILD_HASH}>
                System designed with ❤️ by Squid - {VERSION_LABEL}
              </p>
            </div>
          </div>
        </div>
      </footer>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Desktop Layout */}
      {!isMobile && (
        <div className="flex h-screen">
          {/* Desktop Sidebar */}
          <div className="w-64 bg-white shadow-lg">
            {SidebarContent()}
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Desktop Header */}
            <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-3 flex items-center justify-between">
              {/* Search Bar - Global search across all user pages */}
              <div className="flex-1 max-w-2xl mr-6">
                <form onSubmit={handleSearch} className="flex items-center space-x-3">
                  <div className="flex-1 relative" data-global-search>
                    <AutocompleteInput
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onSelect={handleSearchSuggestionSelect}
                      placeholder="Part number or description…  (press / to focus)"
                      className="w-full"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSearching}
                    className="px-4 py-2 bg-brand text-brand-dark rounded-lg font-medium hover:bg-brand-hover transition-colors duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Search parts"
                  >
                    {isSearching ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </button>
                  {/* Search History & Favourites Dropdown */}
                  <div className="relative" ref={searchHistoryRef}>
                    <button
                      type="button"
                      onClick={() => setShowSearchHistory(!showSearchHistory)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      title="Search History & Favourites"
                      aria-label="Search History & Favourites"
                      aria-expanded={showSearchHistory}
                    >
                      <Clock className="w-5 h-5" />
                    </button>
                    
                    {/* Search History Dropdown */}
                    <AnimatePresence>
                      {showSearchHistory && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                        >
                          <div className="p-4">
                            <div className="flex border-b border-gray-200">
                              <button 
                                onClick={() => setActiveHistoryTab('recent')}
                                className={`flex-1 py-2 text-sm font-medium transition-colors duration-200 ${
                                  activeHistoryTab === 'recent'
                                    ? 'text-gray-900 border-b-2 border-brand'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                Recent
                              </button>
                              <button 
                                onClick={() => setActiveHistoryTab('favorites')}
                                className={`flex-1 py-2 text-sm font-medium transition-colors duration-200 ${
                                  activeHistoryTab === 'favorites'
                                    ? 'text-gray-900 border-b-2 border-brand'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                Favourites
                              </button>
                            </div>
                            
                            {/* Tab Content */}
                            <div className="mt-3 max-h-60 overflow-y-auto">
                              {activeHistoryTab === 'recent' ? (
                                searchHistory.length > 0 ? (
                                  <div className="space-y-1">
                                    {searchHistory.map((item, index) => (
                                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded group">
                                        <button
                                          onClick={() => handleHistoryItemClick(item)}
                                          className="flex-1 text-left text-sm text-gray-700 hover:text-gray-900"
                                        >
                                          {item.part_number}
                                        </button>
                                        <button
                                          onClick={() => handleRemoveFromHistory(item.part_number)}
                                          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-all duration-200"
                                          aria-label={`Remove ${item.part_number} from search history`}
                                        >
                                          <Trash2 className="w-3 h-3" aria-hidden="true" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 text-center py-4">No recent searches</p>
                                )
                              ) : (
                                favorites.length > 0 ? (
                                  <div className="space-y-1">
                                    {favorites.map((partNumber, index) => (
                                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded group">
                                        <button
                                          onClick={() => handleHistoryItemClick({ part_number: partNumber, search_type: 'exact' })}
                                          className="flex-1 text-left text-sm text-gray-700 hover:text-gray-900"
                                        >
                                          {partNumber}
                                          <span className="text-xs text-gray-500 ml-2">(favourite)</span>
                                        </button>
                                        <button
                                          onClick={() => handleRemoveFromFavorites(partNumber)}
                                          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-all duration-200"
                                          aria-label={`Remove ${partNumber} from favourites`}
                                        >
                                          <Trash2 className="w-3 h-3" aria-hidden="true" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 text-center py-4">No favourites yet</p>
                                )
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </form>
              </div>

              {/* Right Side Icons */}
              <div className="flex items-center space-x-3">
                {/* Airbag Countdown */}
                <AirbagCountdown />

                {/* Changelog Widget */}
                <ChangelogWidget variant="icon" onClick={() => setShowChangelog(true)} />

                {/* Cart Icon */}
                <button
                  onClick={() => {
                    history.push('/user/cart');
                  }}
                  className="text-gray-600 hover:text-gray-900 relative p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  title="Shopping Cart"
                  aria-label="Shopping Cart"
                >
                  <ShoppingCart className="h-6 w-6" />
                  <CountBadge count={cartItemCount} tone="brand" />
                </button>
                
                {/* Notifications Icon */}
                <button
                  onClick={() => setShowNotifications(true)}
                  className="text-gray-600 hover:text-gray-900 relative p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  title="Notifications"
                  aria-label="Notifications"
                >
                  <Bell className="h-6 w-6" />
                  <CountBadge count={unreadNotificationsCount} />
                </button>
              </div>
            </div>

            {/* Main Content Area. The footer sits at the end of the scrolling
                content rather than taking a fixed strip of every screen.
                relative: keeps absolute descendants (e.g. sr-only) inside the scroller instead of growing the window. */}
            <div className="relative flex-1 overflow-y-auto flex flex-col">
              <main className="flex-1 p-6">
                <OrderingDisabledBanner className="mb-6" />
                {children}
              </main>
              {UserFooter()}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Layout */}
      {isMobile && (
        <>
          {/* Mobile Header - Clickable Logo */}
          <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2.5 -ml-2.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              aria-label="Open sidebar"
              aria-expanded={sidebarOpen}
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              onClick={() => history.push('/user/dashboard')}
              className="flex items-center hover:bg-gray-100 rounded-lg p-2 transition-colors duration-200"
            >
              <img src="/Logo2.png" alt="MHS Logo" className="h-8 w-auto mr-2" />
              <span className="hidden min-[420px]:inline text-lg font-semibold text-gray-900">MHS Portal</span>
            </button>
            <div className="flex items-center -mr-2.5">
              {/* Search Icon */}
              <button
                onClick={() => setShowMobileSearch(true)}
                className="text-gray-600 hover:text-gray-900 p-2.5 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                title="Search Parts"
                aria-label="Search Parts"
              >
                <Search className="h-6 w-6" />
              </button>
              
              {/* Changelog Widget - Mobile */}
              <ChangelogWidget variant="icon" className="p-2.5" onClick={() => setShowChangelog(true)} />
              
              {/* Cart Icon */}
              <button
                onClick={() => {
                  history.push('/user/cart');
                }}
                className="text-gray-600 hover:text-gray-900 relative p-2.5 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                title="Shopping Cart"
                aria-label="Shopping Cart"
              >
                <ShoppingCart className="h-6 w-6" />
                <CountBadge count={cartItemCount} tone="brand" />
              </button>
              
              {/* Notifications Icon */}
              <button
                onClick={() => setShowNotifications(true)}
                className="text-gray-600 hover:text-gray-900 relative p-2.5 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                title="Notifications"
                aria-label="Notifications"
              >
                <Bell className="h-6 w-6" />
                <CountBadge count={unreadNotificationsCount} />
              </button>
            </div>
          </div>

          {/* Mobile Sidebar */}
          <AnimatePresence>
            {sidebarOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40 bg-black bg-opacity-50"
                  onClick={() => setSidebarOpen(false)}
                />
                <motion.div
                  initial={{ x: '-100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '-100%' }}
                  transition={{ type: 'tween', duration: 0.3 }}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Navigation menu"
                  className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg"
                >
                  {SidebarContent()}
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="relative flex-1 overflow-y-auto p-4">
              <OrderingDisabledBanner className="mb-4" />
              {children}
            </main>
            {UserFooter()}
          </div>
        </>
      )}

      {/* Mobile Search Modal */}
      <AnimatePresence>
        {showMobileSearch && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50"
              onClick={() => setShowMobileSearch(false)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              role="dialog"
              aria-modal="true"
              aria-label="Search parts"
              className="fixed inset-0 z-50 bg-white"
            >
              <div className="flex flex-col h-full">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Search Parts</h2>
                  <button
                    onClick={() => setShowMobileSearch(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                    aria-label="Close search"
                  >
                    <X className="w-6 h-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Search Form */}
                <div className="p-4 border-b border-gray-200">
                  <form onSubmit={handleSearch} className="space-y-4">
                    <AutocompleteInput
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onSelect={handleSearchSuggestionSelect}
                      placeholder="Enter part number or search term..."
                      className="w-full"
                    />
                    <div className="flex space-x-3">
                      <button
                        type="submit"
                        disabled={isSearching}
                        className="flex-1 px-6 py-2.5 bg-brand text-brand-dark rounded-lg font-medium hover:bg-brand-hover transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSearching ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            Searching...
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4 mr-2" />
                            Search
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Recent Searches & Favorites */}
                <div className="flex-1 overflow-y-auto p-4">
                  {/* Recent Searches Section */}
                  {searchHistory.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        Recent Searches
                      </h3>
                      <div className="space-y-2">
                        {searchHistory.slice(0, 5).map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group">
                            <button
                              onClick={() => handleHistoryItemClick(item)}
                              className="flex-1 text-left"
                            >
                              <div className="font-medium text-gray-900">{item.part_number}</div>
                            </button>
                            <button
                              onClick={() => handleRemoveFromHistory(item.part_number)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200"
                              aria-label={`Remove ${item.part_number} from search history`}
                            >
                              <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Favorites Section */}
                  {favorites.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <Star className="w-4 h-4 mr-2" />
                        Favourites
                      </h3>
                      <div className="space-y-2">
                        {favorites.slice(0, 5).map((partNumber, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group">
                            <button
                              onClick={() => handleHistoryItemClick({ part_number: partNumber, search_type: 'exact' })}
                              className="flex-1 text-left"
                            >
                              <div className="font-medium text-gray-900">{partNumber}</div>
                              <div className="text-sm text-gray-500">Favourite part</div>
                            </button>
                            <button
                              onClick={() => handleRemoveFromFavorites(partNumber)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200"
                              aria-label={`Remove ${partNumber} from favourites`}
                            >
                              <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {searchHistory.length === 0 && favorites.length === 0 && (
                    <div className="text-center py-12">
                      <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Start Searching</h3>
                      <p className="text-gray-500">
                        Enter a part number or description above to find the parts you need.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Notifications Modal */}
      <AnimatePresence>
        {showNotifications && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowNotifications(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              role="dialog"
              aria-modal="true"
              aria-label="Notifications"
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              <NotificationTimeline
                onClose={() => setShowNotifications(false)} 
                onNotificationRead={fetchUnreadNotificationsCount}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Changelog Modal */}
      <ChangelogModal 
        isOpen={showChangelog} 
        onClose={() => setShowChangelog(false)} 
      />

      {/* Enhanced Global Search Loading Indicator */}
      <AnimatePresence>
        {isSearching && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-0 left-0 right-0 z-[100] bg-brand shadow-lg"
          >
            {/* Enhanced progress bar */}
            <div className="h-2 bg-brand-dark relative overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-brand to-brand-hover"
                initial={{ x: "-100%" }}
                animate={{ x: "100%" }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              />
            </div>
            
            {/* Loading text */}
            <div className="px-4 py-2 flex items-center justify-center">
              <Loader className="w-4 h-4 mr-2 animate-spin text-brand-dark" />
              <span className="text-sm font-medium text-brand-dark">
                Searching for parts...
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserLayout;
